import React, { useEffect, useRef, useState } from 'react';
import { Input } from './input';
import { loadGoogleMapsPlaces } from '../../lib/googleMapsLoader';

/**
 * PlacesAutocompleteInput
 *
 * Wraps the shadcn <Input> with Google Places Autocomplete so operators
 * pick a real, geocoded address instead of typing free-text (which leads
 * to silent route degradation when Distance Matrix can't resolve a typo).
 *
 * Contract
 * ────────
 *   • Free-text typing is always allowed — autocomplete is an assist, not
 *     a gate. If Google fails to load, the input stays usable.
 *   • On selection (click OR keyboard Enter), `onChange(formattedAddress)`
 *     fires with the chosen Place's `formatted_address` so the parent
 *     stores a clean string. We ALSO push the formatted value back into
 *     the native input.value as a defensive belt — Google sets it to the
 *     short text and the React/Google ordering can race.
 *   • Enter never submits the parent form while the Places dropdown is
 *     visible — we swallow the keydown so Google's own selection wins.
 *   • Optional `onPlaceSelected(place)` exposes the raw Place result for
 *     callers that want lat/lng or place_id later. Most callers ignore it.
 *
 * Notes
 * ─────
 *   • The `.pac-container` dropdown is appended to <body> by Google, NOT
 *     inside whatever Dialog/Sheet renders us. The outside-click guard
 *     that prevents Radix Dialog/Sheet from closing on suggestion click
 *     lives in `lib/googleMapsLoader.js` (installed once after Maps loads).
 *   • Prop callbacks (`onChange`, `onPlaceSelected`) are stashed in refs so
 *     the autocomplete listener — attached once at mount — always calls
 *     the LATEST parent function rather than a stale closure.
 */
export default function PlacesAutocompleteInput({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  disabled,
  className,
  countryRestrictions,
  ...rest
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Keep prop callbacks current for the autocomplete listener closure,
  // which is attached only once at mount.
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsPlaces()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        // Guard against React re-mount races (StrictMode in dev runs effects
        // twice; without this we'd attach two autocomplete instances).
        if (autocompleteRef.current) return;
        const opts = {
          types: ['address'],
          fields: ['formatted_address', 'geometry', 'place_id'],
        };
        if (countryRestrictions && countryRestrictions.length) {
          opts.componentRestrictions = { country: countryRestrictions };
        }
        const ac = new google.maps.places.Autocomplete(inputRef.current, opts);
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          const formatted = place?.formatted_address;
          if (!formatted) return; // user pressed Enter on text with no match — leave raw text alone

          // 1) Push the formatted value to the parent (controlled input)
          onChangeRef.current?.(formatted);
          onPlaceSelectedRef.current?.(place);

          // 2) Defensive: ensure the visible input.value matches the
          //    formatted address even if React's render arrives after
          //    Google's internal `input.value = shortText` write. This
          //    closes the well-known race between Google's native mutation
          //    and React's controlled re-render.
          if (inputRef.current && inputRef.current.value !== formatted) {
            inputRef.current.value = formatted;
          }
        });
        autocompleteRef.current = ac;
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[PlacesAutocomplete] failed to load:', err);
        setLoadError(err.message || 'autocomplete unavailable');
      });
    return () => { cancelled = true; };
    // We intentionally omit countryRestrictions from deps — changing it
    // after mount would require detaching and re-attaching the autocomplete
    // instance, which is unusual in this app. Callers pass a stable array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swallow Enter at all times while the user has focus on the autocomplete
  // input. Two reasons:
  //   1) Prevents the parent <form> from submitting with the user's raw
  //      typed text BEFORE Google's `place_changed` listener has fired.
  //   2) Google's own keydown handler runs alongside ours (preventDefault
  //      does NOT stop the event reaching other listeners — only
  //      stopPropagation does), so Google still selects the highlighted
  //      prediction normally.
  // If the user wants to "submit" with no prediction selected, they click
  // the explicit Save button — which is the correct UX for an autocomplete
  // field anyway.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value ?? ''}
      onChange={(e) => onChangeRef.current?.(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      // Browsers and the Maps dropdown both fight to autofill — turn off
      // native autocomplete so only Google's dropdown surfaces.
      autoComplete="off"
      aria-label="Address"
      title={loadError ? `Autocomplete unavailable — typing still works (${loadError})` : undefined}
      data-places-ready={ready ? '1' : '0'}
      {...rest}
    />
  );
}
